/**
 * @file AI 难度参数表（`design/gdd/06-ai-strategy.md` §2.2 权重表 / §6.1 参数表）。
 *
 * 所有常量按 `06` 文档固化；如需线上热更，请走 `config.ts` 的 `RuleConfig.ai.weights`
 * 分支（开放问题 A-3），不要直接改这里的硬编码表 —— 这里只做默认值。
 */

import type { AIDifficulty } from '../config';
import type { GangType, SeenScope } from './types';

/* ─────────────────────────── §2.2 定缺权重 ─────────────────────────── */

/** 张数权重：张数是定缺的绝对主因。 */
export const W_COUNT = 100;
/** 刻子权重：已成面子，打掉损失最大。 */
export const W_TRIPLET = 60;
/** 对子权重：可成刻可作将。 */
export const W_PAIR = 30;
/** 两面/边张搭子权重：进张多。 */
export const W_CONN = 22;
/** 坎张搭子权重：进张少。 */
export const W_GAP = 10;
/** 孤张权重（**负权**）：孤张打掉反而是效率。 */
export const W_ISOLATED = 15;

/* ─────────────────────────── §5.3 杠的常量 ─────────────────────────── */

/** 根的期望价值（胡牌时每根 ×2 的期望折现）。 */
export const GEN_FAN_VALUE = 1.5;

/** 杠的即时分：明 1 / 暗 2 / 巴 1（`05` 血战到底规则）。 */
export const GANG_SCORE: Readonly<Record<GangType, number>> = {
  MING_GANG: 1,
  AN_GANG: 2,
  BA_GANG: 1,
};

/* ─────────────────────────── §6.1 难度参数表 ─────────────────────────── */

/** 数牌范围（§6.1 数牌能力列）。 */
export type SeenScopeMode = SeenScope;

/** 向听计算范围（§6.1 向听计算列）。 */
export type ShantenMode = 'STANDARD_ONLY' | 'FULL';

/** 进张统计口径（§6.1 进张统计列）。 */
export type UkeireMode = 'NONE' | 'KINDS' | 'TOTAL';

/** 定缺策略（§6.1 定缺策略列）。 */
export type QueMode = 'COUNT_ONLY' | 'FULL' | 'FULL_PLUS';

/** 碰的决策策略（§6.1 碰的决策列）。 */
export type PengMode = 'AGGRESSIVE' | 'SHANTEN' | 'SHANTEN_PLUS';

/** 杠的决策策略（§6.1 杠的决策列）。 */
export type GangMode = 'ALWAYS' | 'NO_REGRESSION' | 'FULL_VALUE';

/** 一档难度的完整预设。 */
export interface DifficultyPreset {
  readonly difficulty: AIDifficulty;
  /** `W_EFF`：牌效率权重（三档恒 1.0）。 */
  readonly wEfficiency: number;
  /** `W_SAFE`：安全度权重基线。困难档为占位（实际由 `dynamicSafeWeight` 动态计算）。 */
  readonly wSafety: number;
  /** `W_YAKU`：番型价值权重。 */
  readonly wYaku: number;
  /** `W_FLEX`：灵活性权重。 */
  readonly wFlex: number;
  /** 随机扰动 ε（§6.3）。 */
  readonly epsilon: number;
  readonly shantenMode: ShantenMode;
  readonly ukeireMode: UkeireMode;
  readonly seenScope: SeenScope;
  /** 是否启用 2 步前瞻（§6.1 前瞻深度列；对应开放问题 A-4）。 */
  readonly useTwoStep: boolean;
  /** 拟人化决策延迟范围 `[min, max]` 毫秒（§6.1 决策延迟列）。 */
  readonly delayRange: readonly [number, number];
  readonly queMode: QueMode;
  readonly pengMode: PengMode;
  readonly gangMode: GangMode;
}

/** 三档难度参数表（`06` §6.1 逐列实现）。 */
export const DIFFICULTY_PRESETS: Readonly<Record<AIDifficulty, DifficultyPreset>> = {
  EASY: {
    difficulty: 'EASY',
    wEfficiency: 1.0,
    wSafety: 0, // 简单 AI 完全不防守
    wYaku: 0,
    wFlex: 0,
    epsilon: 0.35,
    shantenMode: 'STANDARD_ONLY', // 仅标准型
    ukeireMode: 'NONE', // 不算进张
    seenScope: 'HAND_ONLY', // 仅自己手牌
    useTwoStep: false, // 1 步贪心
    delayRange: [300, 800],
    queMode: 'COUNT_ONLY', // 仅张数
    pengMode: 'AGGRESSIVE', // 85% 概率就碰
    gangMode: 'ALWAYS', // 见杠就杠
  },
  NORMAL: {
    difficulty: 'NORMAL',
    wEfficiency: 1.0,
    wSafety: 0.35,
    wYaku: 0.2,
    wFlex: 0.15,
    epsilon: 0.12,
    shantenMode: 'FULL', // 标准型 + 七对
    ukeireMode: 'KINDS', // 进张种数
    seenScope: 'HAND_OWN_DISCARDS', // 手牌 + 自己牌河
    useTwoStep: false, // 1 步 + 进张加权
    delayRange: [500, 1200],
    queMode: 'FULL', // 完整评估函数
    pengMode: 'SHANTEN', // 进向听才碰
    gangMode: 'NO_REGRESSION', // 不退向听才杠
  },
  HARD: {
    difficulty: 'HARD',
    wEfficiency: 1.0,
    wSafety: 0, // 动态 0.4 → 1.8（§6.2，由 dynamicSafeWeight 计算）
    wYaku: 0.65,
    wFlex: 0.3,
    epsilon: 0.02,
    shantenMode: 'FULL', // 标准型 + 七对（同普通）
    ukeireMode: 'TOTAL', // 进张总枚数（liveCount）
    seenScope: 'FULL', // 全场牌河 + 所有明副露
    useTwoStep: true, // 2 步前瞻（A-4 待实测）
    delayRange: [800, 2000],
    queMode: 'FULL_PLUS', // 完整评估 + 番型潜力（清一色规划）
    pengMode: 'SHANTEN_PLUS', // 进向听 + 不破坏番型倾向
    gangMode: 'FULL_VALUE', // 完整 gangValue（含呼叫转移风险）
  },
};
